import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { Modal } from '@app/ui/modal';
import { Alert } from '@app/ui/alert';
import { error as toastError, success as toastSuccess } from '@app/snackbars';

import { RULE_SET_VERSION_GET_ONE, RULE_SET_VERSION_RESTORE_FROM_VERSION } from '../gql';
import { RuleSetVersion, RuleSetStatus } from '../types';
import { familyRuleCount, getGraphQLErrorMessage } from '../utils';

export interface RestoreVersionDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly source: RuleSetVersion;
  readonly activeVersion: RuleSetVersion | null;
  readonly onRestored: () => void;
}

// SPEC D9 — history is append-only, so "roll back to v3" is "load v3's rules into the draft, then
// publish a new version". The dialog has to say that, because the button reads like it makes v3
// live again. When a draft is already open its contents are REPLACED.
export const RestoreVersionDialog = ({
  open,
  onClose,
  source,
  activeVersion,
  onRestored,
}: RestoreVersionDialogProps) => {
  const { t } = useTranslation('common');

  const draftIsOpen = activeVersion?.status === RuleSetStatus.draft;

  const { data: sourceData, loading } = useQuery<{ ruleSetVersionGetOne: RuleSetVersion | null }>(
    RULE_SET_VERSION_GET_ONE,
    { variables: { id: source.id } },
  );

  const [restoreVersion, { loading: restoring }] = useMutation<{
    ruleSetVersionRestoreFromVersion: RuleSetVersion;
  }>(RULE_SET_VERSION_RESTORE_FROM_VERSION);

  // Colour has no `Rule` rows — `.rules.length` is always 0 for it (`familyRuleCount` mirrors the
  // backend's own `FAMILY_CHILDREN[family].count`, scoring + gates).
  const sourceRuleCount = familyRuleCount(source.family, sourceData?.ruleSetVersionGetOne);

  const handleRestore = async () => {
    try {
      await restoreVersion({ variables: { input: { family: source.family, sourceVersionId: source.id } } });
      toastSuccess(t('rules.restore.successMessage', { version: source.version }));
      onRestored();
    } catch (e) {
      toastError(getGraphQLErrorMessage(e) ?? t('general.error.somethingWentWrong'));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      maxWidth="sm"
      title={t('rules.restore.dialogTitle', { version: source.version })}
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <>
          <Typography variant="body1">
            {t('rules.restore.explanation', { count: sourceRuleCount, sourceVersion: source.version })}
          </Typography>

          <Typography variant="body1" sx={{ mt: 1 }}>
            {t('rules.restore.historyNote', { version: source.version })}
          </Typography>

          {draftIsOpen && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t('rules.restore.draftWillBeReplaced', { version: activeVersion?.version })}
            </Alert>
          )}

          <Alert severity="info" sx={{ mt: 2 }}>
            {t('rules.restore.publishReminder')}
          </Alert>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
            <Button onClick={onClose}>{t('rules.restore.cancelButton')}</Button>
            <LoadingButton loading={restoring} variant="contained" color="primary" onClick={handleRestore}>
              {t('rules.restore.confirmButton')}
            </LoadingButton>
          </Box>
        </>
      )}
    </Modal>
  );
};
